(function(scope){
  'use strict';

  var INVALID_CART_RESPONSE_CODE = 'INVALID_CART_RESPONSE';
  var CART_QUANTITY_MISMATCH_CODE = 'CART_QUANTITY_MISMATCH';
  var initialCartReads = new Map();

  function initialCartResponse(url, fetcher){
    var pending = initialCartReads.get(url);
    if (!pending) {
      pending = Promise.resolve().then(function(){
        return fetcher(url, { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
      }).then(function(response){
        if (initialCartReads.get(url) === pending) initialCartReads.delete(url);
        return response;
      }, function(error){
        if (initialCartReads.get(url) === pending) initialCartReads.delete(url);
        throw error;
      });
      initialCartReads.set(url, pending);
    }
    return pending.then(function(response){ return response.clone(); });
  }

  function isNonNegativeInteger(value){
    return Number.isInteger(value) && value >= 0;
  }

  function isNonNegativeNumber(value){
    return typeof value === 'number' && Number.isFinite(value) && value >= 0;
  }

  function isValidCart(cart){
    if (!cart || typeof cart !== 'object' || Array.isArray(cart)) return false;
    if (!Array.isArray(cart.items)) return false;
    if (!isNonNegativeInteger(cart.item_count)) return false;
    if (!isNonNegativeNumber(cart.total_price)) return false;

    var itemCount = 0;
    var keys = new Set();
    for (var index = 0; index < cart.items.length; index += 1){
      var item = cart.items[index];
      if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
      if (typeof item.key !== 'string' || !item.key.trim() || keys.has(item.key)) return false;
      if (!Number.isInteger(item.quantity) || item.quantity < 1) return false;
      if (!isNonNegativeNumber(item.final_price)) return false;
      if (!isNonNegativeNumber(item.final_line_price)) return false;
      keys.add(item.key);
      itemCount += item.quantity;
    }

    return itemCount === cart.item_count;
  }

  function invalidCartResponseError(status){
    var error = new Error('Cart is temporarily unavailable. Please retry in a moment.');
    error.code = INVALID_CART_RESPONSE_CODE;
    error.status = status;
    error.requiresReconciliation = true;
    return error;
  }

  function normalizeProperties(properties){
    if (!properties) return {};
    if (Array.isArray(properties)) {
      return properties.reduce(function(nextProperties, entry){
        var key = entry && (entry.name || entry.key);
        if (!key || entry.value === undefined) return nextProperties;
        nextProperties[key] = entry.value;
        return nextProperties;
      }, {});
    }
    return Object.keys(properties).reduce(function(nextProperties, key){
      if (properties[key] !== undefined) nextProperties[key] = properties[key];
      return nextProperties;
    }, {});
  }

  function propertyValue(properties, key){
    if (!properties || properties[key] === undefined || properties[key] === null) return '';
    return String(properties[key]);
  }

  function matchingProperties(itemProperties, expectedProperties){
    var actual = normalizeProperties(itemProperties);
    var expected = normalizeProperties(expectedProperties);
    var comparableKeys = Object.keys(expected).filter(function(key){
      return key.trim() !== '_Production Filename Template';
    });
    if (!comparableKeys.length) return false;
    return comparableKeys.every(function(key){
      return propertyValue(actual, key) === propertyValue(expected, key);
    });
  }

  function resolveCartLine(cart, context){
    if (!isValidCart(cart)) return null;
    var expected = context || {};
    var items = cart.items;

    if (expected.key) {
      var keyMatch = items.find(function(item){
        return item.key === expected.key;
      });
      if (keyMatch) return keyMatch;
    }

    var stablePropertyKeys = [
      'ID',
      '_Production Payload ID',
      '_OrderID',
      '_Original Image',
      '_Print Ready File'
    ];
    var expectedProperties = normalizeProperties(expected.properties);
    var stableMatches = stablePropertyKeys.filter(function(key){
      return propertyValue(expectedProperties, key);
    });
    if (stableMatches.length) {
      var identityCandidates = items.filter(function(item){
        var itemProperties = normalizeProperties(item.properties);
        return stableMatches.every(function(key){
          return propertyValue(itemProperties, key) === propertyValue(expectedProperties, key);
        });
      });
      if (identityCandidates.length === 1) return identityCandidates[0];
      items = identityCandidates;
    }

    var variantCandidates = items.filter(function(item){
      var variantId = item.variant_id === undefined ? item.id : item.variant_id;
      return expected.variantId !== undefined && String(variantId) === String(expected.variantId);
    });
    var propertyCandidates = variantCandidates.filter(function(item){
      return matchingProperties(item.properties, expectedProperties);
    });
    if (propertyCandidates.length === 1) return propertyCandidates[0];
    if (variantCandidates.length === 1) return variantCandidates[0];
    return null;
  }

  function discountSummary(cart){
    if (!isValidCart(cart)) return null;

    var titles = [];
    var seenTitles = new Set();
    function addTitle(application){
      var title = application && (application.title || application.code);
      var normalized = String(title || '').trim();
      var identity = normalized.toLowerCase();
      if (!normalized || seenTitles.has(identity)) return;
      seenTitles.add(identity);
      titles.push(normalized);
    }

    [
      cart.discount_applications,
      cart.cart_level_discount_applications
    ].forEach(function(applications){
      if (!Array.isArray(applications)) return;
      applications.forEach(addTitle);
    });

    cart.items.forEach(function(item){
      if (Array.isArray(item.line_level_discount_allocations)) {
        item.line_level_discount_allocations.forEach(function(allocation){
          addTitle(allocation && allocation.discount_application);
        });
      }
      if (Array.isArray(item.discounts)) item.discounts.forEach(addTitle);
    });

    var reportedDiscount = isNonNegativeNumber(cart.total_discount) ? cart.total_discount : 0;
    var reportedOriginalTotal = isNonNegativeNumber(cart.original_total_price)
      ? cart.original_total_price
      : cart.total_price + reportedDiscount;
    var calculatedDiscount = Math.max(0, reportedOriginalTotal - cart.total_price);
    var totalDiscount = Math.max(reportedDiscount, calculatedDiscount);
    var originalTotal = Math.max(reportedOriginalTotal, cart.total_price + totalDiscount);

    return {
      finalTotal: cart.total_price,
      hasDiscount: totalDiscount > 0,
      originalTotal: originalTotal,
      titles: titles,
      totalDiscount: totalDiscount
    };
  }

  function snapshotState(state){
    return {
      initialKey: state.initialKey,
      currentKey: state.currentKey,
      confirmedQuantity: state.confirmedQuantity,
      desiredQuantity: state.desiredQuantity,
      meta: state.meta || {},
      running: state.running
    };
  }

  function isQueueResult(result){
    if (!result || typeof result !== 'object') return false;
    if (!isNonNegativeInteger(result.quantity)) return false;
    if (result.quantity > 0 && (typeof result.key !== 'string' || !result.key)) return false;
    return true;
  }

  function quantityMismatchError(requestedQuantity, confirmedQuantity){
    var error = new Error('The requested quantity was not confirmed. Please review the cart and try again.');
    error.code = CART_QUANTITY_MISMATCH_CODE;
    error.requestedQuantity = requestedQuantity;
    error.confirmedQuantity = confirmedQuantity;
    return error;
  }

  function createQuantityQueue(options){
    if (!options || typeof options.mutate !== 'function' || typeof options.reconcile !== 'function') {
      throw new TypeError('Cart quantity queue requires mutate and reconcile functions.');
    }

    var statesByKey = new Map();
    var scheduledDrains = [];
    var schedulerRunning = false;

    function findState(key){
      if (statesByKey.has(key)) return statesByKey.get(key);
      var states = Array.from(new Set(statesByKey.values()));
      return states.find(function(state){
        return state.initialKey === key || state.currentKey === key;
      }) || null;
    }

    function rememberCurrentKey(state, key){
      if (!key) return;
      state.currentKey = key;
      statesByKey.set(key, state);
    }

    function forgetState(state){
      Array.from(statesByKey.entries()).forEach(function(entry){
        if (entry[1] === state) statesByKey.delete(entry[0]);
      });
    }

    async function drain(state){
      var completed = false;
      var lastError = null;
      try {
        while (true) {
          var requestedQuantity = state.desiredQuantity;
          var requestedMeta = state.meta || {};
          var result;
          var recovered = false;
          var mutationError = null;

          try {
            result = await options.mutate({
              key: state.currentKey,
              quantity: requestedQuantity,
              state: snapshotState(state)
            });
          } catch(error) {
            mutationError = error;
            try {
              result = await options.reconcile({
                key: state.currentKey,
                quantity: requestedQuantity,
                error: error,
                state: snapshotState(state)
              });
              recovered = true;
            } catch(reconciliationError) {
              reconciliationError.mutationError = error;
              lastError = reconciliationError;
              if (typeof options.onError === 'function') {
                options.onError(reconciliationError, snapshotState(state), null, {
                  requestedMeta: requestedMeta,
                  requestedQuantity: requestedQuantity
                });
              }
              return;
            }
          }

          if (!isQueueResult(result)) {
            lastError = invalidCartResponseError();
            if (typeof options.onError === 'function') {
              options.onError(lastError, snapshotState(state), null, {
                requestedMeta: requestedMeta,
                requestedQuantity: requestedQuantity
              });
            }
            return;
          }

          rememberCurrentKey(state, result.key);
          state.confirmedQuantity = result.quantity;
          var settled = state.desiredQuantity === state.confirmedQuantity;

          if (typeof options.onConfirmed === 'function') {
            options.onConfirmed(result, snapshotState(state), {
              recovered: recovered,
              requestedMeta: requestedMeta,
              requestedQuantity: requestedQuantity,
              settled: settled
            });
          }

          if (state.confirmedQuantity !== requestedQuantity) {
            lastError = mutationError || quantityMismatchError(requestedQuantity, state.confirmedQuantity);
            if (typeof options.onError === 'function') {
              options.onError(lastError, snapshotState(state), result, {
                requestedMeta: requestedMeta,
                requestedQuantity: requestedQuantity
              });
            }
            return;
          }

          if (settled) {
            completed = true;
            return;
          }
        }
      } finally {
        state.running = false;
        forgetState(state);
        if (typeof options.onSettled === 'function') {
          options.onSettled(snapshotState(state), {
            completed: completed,
            error: lastError
          });
        }
      }
    }

    async function runScheduler(){
      if (schedulerRunning) return;
      schedulerRunning = true;
      try {
        while (scheduledDrains.length) {
          var scheduled = scheduledDrains.shift();
          try {
            scheduled.resolve(await drain(scheduled.state));
          } catch(error) {
            scheduled.reject(error);
          }
        }
      } finally {
        schedulerRunning = false;
      }
    }

    function scheduleDrain(state){
      var promise = new Promise(function(resolve, reject){
        scheduledDrains.push({
          state: state,
          resolve: resolve,
          reject: reject
        });
      });
      runScheduler();
      return promise;
    }

    function request(requestOptions){
      var key = requestOptions && String(requestOptions.key || '');
      var quantity = requestOptions && Number(requestOptions.quantity);
      if (!key || !isNonNegativeInteger(quantity)) {
        return Promise.reject(new TypeError('A cart line key and non-negative integer quantity are required.'));
      }

      var state = findState(key);
      if (!state) {
        var confirmedQuantity = Number(requestOptions.confirmedQuantity);
        state = {
          initialKey: key,
          currentKey: key,
          confirmedQuantity: isNonNegativeInteger(confirmedQuantity) ? confirmedQuantity : quantity,
          desiredQuantity: quantity,
          meta: requestOptions.meta || {},
          running: false,
          promise: null
        };
        statesByKey.set(key, state);
      }

      state.desiredQuantity = quantity;
      state.meta = requestOptions.meta || state.meta || {};
      if (typeof options.onPending === 'function') {
        options.onPending(snapshotState(state));
      }

      if (!state.running) {
        state.running = true;
        state.promise = scheduleDrain(state);
      }
      return state.promise;
    }

    return {
      request: request,
      hasPending: function(key){
        if (key) return Boolean(findState(String(key)));
        return statesByKey.size > 0;
      }
    };
  }

  var api = {
    CART_QUANTITY_MISMATCH_CODE: CART_QUANTITY_MISMATCH_CODE,
    INVALID_CART_RESPONSE_CODE: INVALID_CART_RESPONSE_CODE,
    createQuantityQueue: createQuantityQueue,
    discountSummary: discountSummary,
    invalidCartResponseError: invalidCartResponseError,
    initialCartResponse: initialCartResponse,
    isValidCart: isValidCart,
    normalizeProperties: normalizeProperties,
    resolveCartLine: resolveCartLine
  };

  scope.StackhouseCartDrawerState = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
